import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUserStore, useProfileStore } from '../stores'
import { Capacitor } from '@capacitor/core'
import {
  decideOnReturn,
  isSetupInProgress,
  RETURN_CONTINUE,
  RETURN_LOCK,
  RETURN_LOGOUT,
} from '../utils/sessionReturn'

/**
 * usePrivacyScreen — the AWAY/RETURN owner, and nothing else.
 *
 * v187.0: HIPAA 164.310(b) — cover PHI when the app leaves the foreground.
 * v242.130.0: and decide what happens when it comes BACK, through one pure
 *             function instead of three scattered clocks.
 *
 * ★★★ WHAT WAS BROKEN, AND IT WAS NOT THE TIMER. v242.128.0 fixed the foreground
 *     inactivity countdown (`useIdleTimeout`) and did not touch this hook. Two
 *     things here meant the away clock was usually MISSING at the moment the
 *     patient returned, and a missing clock fell through to "carry on":
 *
 *     (1) The clock was `hiddenAtRef`, a component-local ref. This hook mounts
 *         only inside `MobileLayout`, and `App.jsx` renders
 *         `<Routes location={location} key={location.pathname}>` — the key forces
 *         the ENTIRE route tree to unmount and remount on every navigation. The
 *         ref died with it, and the post-login grace period, which keys off this
 *         hook's mount, restarted as well. So for three seconds after every
 *         single screen change, `goHidden` returned early WITHOUT recording that
 *         the patient had left. Leave the app in that window and you came back to
 *         the page you left, however long you were gone. MEASURED on device,
 *         2026-08-24: idle 10s then background 2 min → PIN (correct); navigate
 *         then background within 2s → straight back to the page (the defect).
 *
 *     (2) ANY `visible` signal cleared the clock. On iOS the app-switcher
 *         snapshot can produce a hidden→visible pair while the app is on its way
 *         to being suspended, which cleared a clock that had just been set.
 *
 * ★★★ THE FIXES ARE STRUCTURAL, NOT ARITHMETIC.
 *     - The stamp lives in the persisted store (`awayAt`), so it outlives the
 *       remount, the route change, and a cold start.
 *     - The grace period keys off `authAt`, the REAL sign-in moment, so it means
 *       "3 seconds after login" again rather than "after every navigation".
 *     - ON A NATIVE BUILD, ONLY `appStateChange` DECIDES. `visibilitychange` may
 *       still STAMP (it is a real away-signal) but it may never clear or judge,
 *       so a spurious visible pair cannot discard a live clock. On the web,
 *       where there is no app state, visibility is the authority.
 *     - The verdict itself is `decideOnReturn`, which is pure, takes `now`, and
 *       is gated by `scripts/check-session-lock.mjs`. The rule this hook used to
 *       hold lived inside a `useEffect` and could not be measured at all.
 */

const AUTH_GRACE_PERIOD_MS = 3000 // ignore away-signals for 3s after a real login

// PROD-1192 (Alex 2026-10-19): during sign-up and setup an app switch (SMS / e-mail code,
// the camera for ID and insurance, Settings) never leads to the PIN lock. Read at the
// moment of the signal, from the app's setup-complete flag (profile.onboardingCompleted).
const setupInProgressNow = () => isSetupInProgress(useProfileStore.getState().profile)

export default function usePrivacyScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)

  const [showPrivacyOverlay, setShowPrivacyOverlay] = useState(false)
  const pathnameRef = useRef(location.pathname)
  useEffect(() => { pathnameRef.current = location.pathname }, [location.pathname])

  useEffect(() => {
    if (!isAuthenticated) {
      setShowPrivacyOverlay(false)
      return
    }

    const isNative = !!(Capacitor.isNativePlatform && Capacitor.isNativePlatform())

    const goHidden = () => {
      const { authAt, stampAway } = useUserStore.getState()
      // Grace period — a real login, not a remount. See the header.
      if (authAt && Date.now() - authAt < AUTH_GRACE_PERIOD_MS) return
      // PROD-1192: no away stamp while setup is not complete; the overlay still shows.
      if (!setupInProgressNow()) stampAway(Date.now())   // idempotent: first away-signal wins
      setShowPrivacyOverlay(true)
    }

    // `authoritative` is false for a web visibility event on a native build.
    const goVisible = (authoritative) => {
      const state = useUserStore.getState()
      const awayAt = state.awayAt

      if (awayAt == null) { setShowPrivacyOverlay(false); return }
      if (!authoritative) return   // stamp survives; only a real resume judges it

      const verdict = decideOnReturn({
        awayAt,
        now: Date.now(),
        pinEnabled: !!state.user?.sessionPinEnabled,
        pinWindowMinutes: state.user?.sessionPinWindow,
        setupInProgress: setupInProgressNow(),
      })

      if (verdict === RETURN_LOCK) {
        state.lockSession(pathnameRef.current, awayAt)
        state.clearAway()
        setShowPrivacyOverlay(false)
        return
      }

      if (verdict === RETURN_LOGOUT) {
        // ★ A full logout, not an overlay with a button on it. The old re-auth
        //   path left the token in storage until the patient chose to tap.
        state.clearAway()
        state.logout()
        setShowPrivacyOverlay(false)
        navigate('/signin', { replace: true })
        return
      }

      // RETURN_CONTINUE
      state.clearAway()
      setShowPrivacyOverlay(false)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') goHidden()
      else if (document.visibilityState === 'visible') goVisible(!isNative)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    let cancelled = false
    let appHandle = null
    if (isNative) {
      import('@capacitor/app').then(({ App }) => {
        if (cancelled) return
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) goVisible(true)
          else goHidden()
        }).then((h) => {
          if (cancelled) h.remove()
          else appHandle = h
        })
      }).catch(() => {})
    }

    // ★★ A resume that arrives before the listeners are re-attached is still a
    //    resume. The stamp is in the store, so a decision taken on mount is the
    //    same decision — this closes the window where the dynamic import had not
    //    resolved yet at the moment the app came back.
    if (document.visibilityState === 'visible') goVisible(true)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (appHandle) appHandle.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const dismissOverlay = useCallback(() => setShowPrivacyOverlay(false), [])

  return { showPrivacyOverlay, dismissOverlay }
}
